import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  OnboardingStatus,
  type OnboardingStatsDto,
  type OnboardingSubmissionDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useIntro } from '../lib/intro';
import { useT } from '../lib/i18n';
import { STATUS_CLASS, useSourceLabels, useStatusLabels, useFormatDate } from './status';
import { PersonCell } from '../components/PersonCell';

const STATUS_VALUES = Object.values(OnboardingStatus) as string[];

/** The status summary cards, in display order. `status: null` = Total (clears). */
const STAT_CARDS: {
  labelKey: string;
  tone: string;
  status: OnboardingStatus | null;
  key: keyof OnboardingStatsDto;
  icon: React.ReactNode;
}[] = [
  {
    labelKey: 'onboarding.submissions.cards.pendingReview',
    tone: 'pending',
    status: OnboardingStatus.PendingReview,
    key: 'pendingReview',
    icon: <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />,
  },
  {
    labelKey: 'onboarding.submissions.cards.changesRequested',
    tone: 'changes',
    status: OnboardingStatus.ChangesRequested,
    key: 'changesRequested',
    icon: <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />,
  },
  {
    labelKey: 'onboarding.submissions.cards.pendingSistrade',
    tone: 'approved',
    status: OnboardingStatus.Approved,
    key: 'approved',
    icon: <path d="M20 6 9 17l-5-5" />,
  },
  {
    labelKey: 'onboarding.submissions.cards.launched',
    tone: 'synced',
    status: OnboardingStatus.LaunchedInSistrade,
    key: 'launched',
    icon: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />,
  },
  {
    labelKey: 'onboarding.submissions.cards.rejected',
    tone: 'rejected',
    status: OnboardingStatus.Rejected,
    key: 'rejected',
    icon: <path d="M18 6 6 18M6 6l12 12" />,
  },
  {
    labelKey: 'onboarding.submissions.cards.total',
    tone: 'total',
    status: null,
    key: 'total',
    icon: <path d="M3 3v18h18M7 14l4-4 3 3 5-5" />,
  },
];

export function Submissions() {
  const t = useT();
  const statusLabels = useStatusLabels();
  const sourceLabels = useSourceLabels();
  const formatDate = useFormatDate();
  const [params] = useSearchParams();
  // Allow deep-linking a status filter (?status=…).
  const initialStatus = params.get('status') ?? '';
  const [rows, setRows] = useState<OnboardingSubmissionDto[]>([]);
  const [stats, setStats] = useState<OnboardingStatsDto | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(STATUS_VALUES.includes(initialStatus) ? initialStatus : '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intro = useIntro() ? ' intro' : '';

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(
        await api.listSubmissions({ search: search || undefined, status: status || undefined }),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // Totals are independent of the active filter, so load them once.
  useEffect(() => {
    api
      .stats()
      .then(setStats)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /** Click a card: filter by that status, or clear when it's Total / already active. */
  function pick(cardStatus: OnboardingStatus | null) {
    setStatus((cur) => (cardStatus === null || cur === cardStatus ? '' : cardStatus));
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('onboarding.submissions.title')}</h2>
          <p className="page-sub">
            {loading
              ? t('onboarding.submissions.loading')
              : rows.length === 1
                ? t('onboarding.submissions.countOne', { n: rows.length })
                : t('onboarding.submissions.countOther', { n: rows.length })}
          </p>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className={`cards${intro}`}>
        {STAT_CARDS.map((c) => {
          const selected = c.status === null ? status === '' : status === c.status;
          return (
            <button
              key={c.key}
              type="button"
              className={`stat-card ${c.tone}${selected ? ' selected' : ''}`}
              onClick={() => pick(c.status)}
              aria-pressed={selected}
            >
              <div className="stat-top">
                <span className="stat-label">{t(c.labelKey)}</span>
                <span className="stat-ic">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {c.icon}
                  </svg>
                </span>
              </div>
              <span className="stat-value">{stats?.[c.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      <div className="erp-panel" style={{ marginTop: 20 }}>
        <div className="erp-toolbar">
          <form
            className="erp-toolbar-actions"
            onSubmit={(e) => {
              e.preventDefault();
              void load();
            }}
          >
            <label className="erp-search">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
              </svg>
              <input
                placeholder={t('onboarding.submissions.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {status && (
              <span className="page-sub" style={{ whiteSpace: 'nowrap' }}>
                {t('onboarding.submissions.filter', {
                  label: statusLabels[status as OnboardingStatus],
                })}
              </span>
            )}
            <button type="submit">{t('onboarding.submissions.searchButton')}</button>
          </form>
        </div>

        {loading ? (
          <div className="panel-empty">{t('onboarding.submissions.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="panel-empty is-empty">{t('onboarding.submissions.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('onboarding.submissions.columns.name')}</th>
                  <th>{t('onboarding.submissions.columns.email')}</th>
                  <th>{t('onboarding.submissions.columns.department')}</th>
                  <th>{t('onboarding.submissions.columns.source')}</th>
                  <th>{t('onboarding.submissions.columns.status')}</th>
                  <th>{t('onboarding.submissions.columns.received')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <PersonCell name={r.fullName}>
                        <Link to={`/submissions/${r.id}`}>{r.fullName}</Link>
                      </PersonCell>
                    </td>
                    <td>{r.email}</td>
                    <td>{r.department ?? '—'}</td>
                    <td>{sourceLabels[r.source]}</td>
                    <td>
                      <span className={STATUS_CLASS[r.status]}>{statusLabels[r.status]}</span>
                    </td>
                    <td>{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
